<script lang="ts">
  export let item: any;
  export let MonthAndYear: any;
  export let sequence: number = 1;
  export let total: number = 1;
  import { isLoading, lang } from "@/store";
  import { axiosInstance } from "@/plugins/axios";
  import { i18n } from "@/plugins/util";
  import AlertModal from "@/components/appDriver/driverTax/AlertModal.svelte";
  import LoadingOverlay from "@/components/appDriver/driverTax/LoadingOverlay.svelte";
  import PdfPreviewModal from "@/components/appDriver/driverTax/PdfPreviewModal.svelte";

  let isDownloading = false;
  let isMenuOpen = false;

  // --- MODAL STATE ---
  let modalState = {
    isOpen: false,
    type: 'success' as 'success' | 'error' | 'info',
    title: '',
    message: '',
    copyUrl: ''
  };

  let pdfPreviewState = {
    isOpen: false,
    url: '',
    title: ''
  };

  const openModal = (type: 'success' | 'error' | 'info', title: string, message: string, copyUrl = '') => {
    modalState = { isOpen: true, type, title, message, copyUrl };
  };
  // -------------------

  function clickOutside(node: HTMLElement) {
    const handleClick = (event: MouseEvent) => {
      if (
        node &&
        !node.contains(event.target as Node) &&
        !event.defaultPrevented
      ) {
        node.dispatchEvent(new CustomEvent("click_outside", { detail: node }));
      }
    };
    document.addEventListener("click", handleClick, true);
    return {
      destroy() {
        document.removeEventListener("click", handleClick, true);
      },
    };
  }

  const handleFileAction = async (filePath: string, pdfName: string, action: 'download' | 'copy') => {
    isDownloading = true;
    isMenuOpen = false;

    try {
      const requestBody = { fileKey: filePath };
      const { data: response } = await axiosInstance.post(
        "/api/v1/driverSalaryFile/imageUrl",
        requestBody,
        { baseURL: import.meta.env.VITE_API_LOCA_URL },
      );

      const url = response?.data?.imageUrl;
      if (!url) throw new Error("URL missing from API");

      // CASE A: COPY LINK
      if (action === 'copy') {
        openModal('info', 'ຄັດລອກລິ້ງ', 'ກົດປຸ່ມດ້ານລຸ່ມເພື່ອຄັດລອກລິ້ງ.', url);
        return;
      }

      // CASE B: DOWNLOAD / PREVIEW
      const isAndroid = /Android/i.test(navigator.userAgent);

      if (isAndroid) {
        // --- ANDROID FIX: SHOW IFRAME MODAL ---
        pdfPreviewState = {
            isOpen: true,
            url: url,
            title: pdfName
        };
        return;
      }

      // --- DESKTOP / iOS DOWNLOAD LOGIC (Unchanged) ---
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error("File fetch failed");
      const blob = await fileResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const finalFileName = pdfName.toLowerCase().endsWith(".pdf") ? pdfName : `${pdfName}.pdf`;

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = finalFileName;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(blobUrl); }, 100);

    } catch (error) {
      console.warn("Action failed:", error);
      openModal('error', 'Action Failed', i18n($lang, ["ການດຳເນີນການລົ້ມເຫຼວ. ກະລຸນາລອງໃໝ່.", "Operation failed."]));
    } finally {
      isDownloading = false;
    }
  };

  // A driver can have more than one ACTIVE salary record for the same month
  // (e.g. mid-month + end-month payout), so filenames must stay unique per record.
  $: fileNameSuffix = item._id ? `-${String(item._id).slice(-6)}` : '';
</script>

<div
  class="bg-white h-20 rounded-md shadow-md flex items-center px-4 mb-4 relative z-5"
>
  <img
    src={new URL("@/assets/driver/pdf.png", import.meta.url).href}
    width="40"
    class="mr-4"
    alt="PDF Icon"
  />

  <div class="flex-1">
    <p class="font-semibold">
      ໃບແຈ້ງເງິນເດືອນ
       {MonthAndYear.title}
      {#if total > 1}
        <span class="text-sm font-normal text-gray-400">({sequence}/{total})</span>
      {/if}
    </p>
  </div>

  <div class="relative">
    <button
      on:click|stopPropagation={() => (isMenuOpen = !isMenuOpen)}
      class="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
        />
      </svg>
    </button>

    {#if isMenuOpen}
      <div
        use:clickOutside
        on:click_outside={() => (isMenuOpen = false)}
        class="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50 origin-top-right animate-fade-in-down"
      >
        <button
          on:click={() =>
            handleFileAction(
              item.filePath,
              `Salary-${item.month}-${item.year}${fileNameSuffix}`,
              "download",
            )}
          class="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
          ເບິ່ງຕົວຢ່າງ PDF
        </button>

        <div class="border-t border-gray-100"></div>

        <button
          on:click={() =>
            handleFileAction(
              item.filePath,
              `Salary-${item.month}-${item.year}${fileNameSuffix}`,
              "copy",
            )}
          class="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          ຄັດລອກລິ້ງ PDF
        </button>
      </div>
    {/if}
  </div>
</div>

<PdfPreviewModal
    isOpen={pdfPreviewState.isOpen}
    pdfUrl={pdfPreviewState.url}
    title={pdfPreviewState.title}
    on:close={() => pdfPreviewState.isOpen = false}
/>

<AlertModal
  isOpen={modalState.isOpen}
  type={modalState.type}
  title={modalState.title}
  message={modalState.message}
  copyUrl={modalState.copyUrl}
  on:close={() => (modalState.isOpen = false)}
/>

{#if isDownloading}
  <LoadingOverlay message="Processing..." />
{/if}

<style>
  .animate-fade-in-down {
    animation: fadeInDown 0.15s ease-out;
  }
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
